import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useRecordContext, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface Duplicate {
  kind: "contact" | "company";
  id: number;
  name: string;
  matched_on: "email" | "phone" | "name";
}

const useDebounced = <T,>(value: T, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

// Possible duplicates while typing (task 1.5, D-033): people by email or
// phone, organizations by name. Only a warning with a link: saving is never
// blocked (D-018, D-031). The database returns no email or phone values.
export const DuplicateWarning = ({ kind }: { kind: "contact" | "company" }) => {
  const translate = useTranslate();
  const record = useRecordContext<{ id?: number }>();
  const emails = useWatch({ name: "email_jsonb" }) as
    | { email?: string }[]
    | undefined;
  const phones = useWatch({ name: "phone_jsonb" }) as
    | { number?: string }[]
    | undefined;
  const name = useWatch({ name: "name" }) as string | undefined;

  const args = useDebounced(
    kind === "contact"
      ? {
          p_emails: (emails ?? []).map((item) => item?.email ?? ""),
          p_phones: (phones ?? []).map((item) => item?.number ?? ""),
          p_exclude_contact_id: record?.id ?? null,
        }
      : {
          p_company_name: name ?? "",
          p_exclude_company_id: record?.id ?? null,
        },
  );

  const { data: duplicates = [] } = useQuery({
    queryKey: ["tenax_find_duplicates", args],
    meta: { persist: false },
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "tenax_find_duplicates",
        args,
      );
      if (error) throw new Error(error.message);
      return data as Duplicate[];
    },
  });

  if (duplicates.length === 0) return null;

  return (
    <Alert>
      <AlertTitle>{translate("tenax.people.duplicates.title")}</AlertTitle>
      <AlertDescription>
        <ul className="flex flex-col gap-1">
          {duplicates.map((duplicate) => (
            <li
              key={`${duplicate.kind}-${duplicate.id}-${duplicate.matched_on}`}
            >
              <Link
                className="underline"
                to={`/${duplicate.kind === "contact" ? "contacts" : "companies"}/${duplicate.id}/show`}
                target="_blank"
              >
                {duplicate.name}
              </Link>{" "}
              (
              {translate(
                `tenax.people.duplicates.matched_on.${duplicate.matched_on}`,
              )}
              )
            </li>
          ))}
        </ul>
        <p className="mt-1">{translate("tenax.people.duplicates.hint")}</p>
      </AlertDescription>
    </Alert>
  );
};
